import { CSpinner } from "@coreui/react";
import React, { useEffect, useReducer, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import Pagination from "../../components/ui/Pagination";
import RenderTable from "../../components/ui/RenderTable";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { Chip, toQuery } from "../../helpers/general";
import { useSelectedPlace } from "../../hooks/place/useSelectedPlace";
import { useAuth } from "../../hooks/useAuth";
import { useCookie } from "../../hooks/useCookie";
import { useDebounce } from "../../hooks/useDebounce";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import RestrictedRoute from "../auth/RestrictedRoute";
import InspectorsTable from "./InspectorsTable";
import { cardStyle } from "../Places/PlacesTable";
import SearchBox from "../../components/ui/SearchBox";

const PAGE_SIZE = 10;

export default function Inspectors() {
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();
  const { isAdmin, user } = useAuth();
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const query = useDebounce(searchQuery, 300);

  useEffect(() => {
    setCurrentPage(1);
  }, [query]);

  const { data, isLoading } = useQuery(
    ["getInspectors", currentPage, query],
    () =>
      apiService.MakeGetRequest(
        `inspectors${toQuery({ page: currentPage, limit: PAGE_SIZE, query })}`,
        token,
      ),
    { keepPreviousData: true },
  );

  const deleteInspector = useMutation((id: string) =>
    apiService.MakeDeleteRequest(
      `inspectors/${id}?placeId=${selectedPlaceId}`,
      token,
    ),
  );

  const [selected, dispatch] = useReducer(bulkDeleteReducer, bulkDeleteState);
  const selectedCount = selected?.ids?.length ?? 0;

  const handleBulkDelete = async () => {
    const ids = selected.ids.filter(Boolean) as string[];
    const results = await Promise.allSettled(
      ids.map((id) => deleteInspector.mutateAsync(id)),
    );

    // Only the inspectors really deleted leave the selection
    results.forEach((r, i) => {
      if (r.status === "fulfilled")
        dispatch({ type: DELETE_ACTIONS.REMOVE, id: ids[i] });
    });

    if (results.some((r) => r.status === "rejected")) {
      toastError(general.fr.message.operationFailed);
    } else {
      toastSuccess(general.fr.message.inspectorDeleted);
    }
    queryClient.invalidateQueries("getInspectors");
  };

  const items: Inspector[] = data?.items ?? [];
  const total = data?.meta?.itemCount ?? data?.meta?.totalItems ?? 0;

  return (
    <RestrictedRoute>
      <section>
        {/* Title + count */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <h5 className="fs-4 fw-bold mb-0">Inspecteurs</h5>
          {data && (
            <Chip tone="warning">
              {total} {total > 1 ? "Inspecteurs" : "Inspecteur"}
            </Chip>
          )}
        </div>

        {/* Toolbar */}
        <div
          style={{ ...cardStyle, padding: "12px 16px" }}
          className="d-flex flex-wrap align-items-center gap-2 mb-3"
        >
          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Rechercher un inspecteur"
          />

          <button
            type="button"
            disabled={deleteInspector.isLoading || selectedCount === 0}
            onClick={handleBulkDelete}
            className="btn btn-outline-danger rounded-pill ms-auto d-flex align-items-center gap-2 px-3"
          >
            {deleteInspector.isLoading ? (
              <>
                <CSpinner size="sm" /> {general.fr.wait}
              </>
            ) : (
              <>
                <DeleteIcon fill={colors.primary} /> Supprimer
                {selectedCount > 0 && ` (${selectedCount})`}
              </>
            )}
          </button>
        </div>

        {/* Table */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          <RenderTable
            loading={isLoading}
            render={() =>
              items.length > 0 ? (
                <InspectorsTable
                  Inspectors={items}
                  InspectorsTableToBeDeleted={dispatch}
                />
              ) : (
                <p className="text-center text-muted py-5 mb-0">
                  {general.fr.noData}
                </p>
              )
            }
          />
        </div>

        <Pagination
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          siblingCount={1}
          totalCount={data?.meta?.totalPages}
          pageSize={PAGE_SIZE}
        />
      </section>
    </RestrictedRoute>
  );
}
